import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface User {
  id: string
  email: string
  name: string
  role: string
  tenant_id?: string
  must_change_password: boolean
}

interface AuthState {
  token: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: boolean
  setAuth: (token: string, refreshToken: string, user: User) => void
  clearAuth: () => void
  updateUser: (user: Partial<User>) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,

      setAuth: (token, refreshToken, user) => {
        localStorage.setItem('token', token)
        localStorage.setItem('refresh_token', refreshToken)
        set({
          token,
          refreshToken,
          user,
          isAuthenticated: true,
        })
      },

      clearAuth: () => {
        localStorage.removeItem('token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('user')
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        })
      },

      updateUser: (userData) => {
        const currentUser = get().user
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)

// Helper to check if user has a specific role
export function hasRole(role: string): boolean {
  const user = useAuthStore.getState().user
  if (!user) return false

  // Platform roles have higher privileges
  const platformRoles = ['super_admin', 'platform_admin', 'platform_support']
  if (platformRoles.includes(user.role)) {
    return true
  }

  return user.role === role
}

// Helper to check if user is platform user
export function isPlatformUser(): boolean {
  const user = useAuthStore.getState().user
  if (!user) return false

  const platformRoles = ['super_admin', 'platform_admin', 'platform_support']
  return platformRoles.includes(user.role)
}
